import { AlertCircle, RefreshCw, Upload, XCircle } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import type { TranslatedCode } from "../../types/payoutBatches.types";

export interface BatchValidationFailedPanelProps {
  blockers: TranslatedCode[];
  failureReason: string | null;
  /** Omitted when the principal cannot upload (`payout_batches:write`): the button is not shown. */
  onUploadAnother?: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
  /**
   * Runs the validation again over the stored rows (`POST /:id/revalidate`), for a cause that was fixed
   * outside the file — the organization got verified, a rail was switched back on. Omitted without
   * `payout_batches:write` or when the upload itself was rejected (no stored rows to validate): the button is
   * not shown.
   */
  onRevalidate?: () => void;
  isRevalidating?: boolean;
}

/** The file could not be validated: every blocker the backend recorded, then the ways out. */
export function BatchValidationFailedPanel({
  blockers,
  failureReason,
  onUploadAnother,
  onCancel,
  isCancelling = false,
  onRevalidate,
  isRevalidating = false,
}: BatchValidationFailedPanelProps) {
  const { t } = useTranslations("payoutBatches");
  // Staff also receive `failure_reason`, which repeats the first blocker's code when the validation job recorded
  // blockers; only a reason no blocker already explains is worth a line of its own.
  const extraReason =
    failureReason && !blockers.some((blocker) => blocker.code === failureReason) ? [failureReason] : [];
  const reasons = blockers.map((blocker) => blocker.message).concat(extraReason);

  return (
    <ColocaSurface padding="lg" className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">{t("validation_failed_title")}</h2>
        <p className="text-sm text-muted-foreground">
          {t(onRevalidate ? "validation_failed_description_revalidate" : "validation_failed_description")}
        </p>
      </div>
      {reasons.length > 0 && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <ul className="list-disc space-y-1 pl-4">
              {reasons.map((reason, index) => (
                <li key={`${index}-${reason}`}>{reason}</li>
              ))}
            </ul>
          }
        />
      )}
      <div className="flex flex-wrap gap-2">
        {onRevalidate && (
          <ColocaButton variant="default" icon={RefreshCw} loading={isRevalidating} onClick={onRevalidate}>
            {t("action_validate_again")}
          </ColocaButton>
        )}
        {onUploadAnother && (
          <ColocaButton variant="primary" icon={Upload} onClick={onUploadAnother}>
            {t("action_upload_another")}
          </ColocaButton>
        )}
        {onCancel && (
          <ColocaButton variant="ghost" icon={XCircle} loading={isCancelling} onClick={onCancel}>
            {t("action_cancel_batch")}
          </ColocaButton>
        )}
      </div>
    </ColocaSurface>
  );
}
